// AppInsights.js
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { v4 as uuidv4 } from 'uuid';

const reactPlugin = new ReactPlugin();

/**
 * Init session account id
 */
const getAccountSessionId = async () => {
    // Get user_AccountId
    let sessionId: string = '';
    if (!sessionId && sessionStorage) {
        if (sessionStorage.getItem('user_sess_id')) {
            sessionId = sessionStorage.getItem('user_sess_id') || '';
        } else {
            sessionId = uuidv4();
            sessionStorage.setItem('user_sess_id', sessionId);
        }
    }

    return sessionId;
};

/**
 * Initialize the instance of Application Insights
 */
const appInsights = (async () => {
    const accountId = await getAccountSessionId();
    const appInsights = new ApplicationInsights({
        config: {
            instrumentationKey: `${INSTRUMENTATIONKEY}`,
            extensions: [reactPlugin],
            accountId,
        },
    });
    appInsights.loadAppInsights();
    // appInsights.trackPageView();
    return appInsights;
})();

/**
 * BNApplicationInsightsFilter API for MyHKBN
 */
export class AppInsightsFilter {
    /**
     * 按filterJson将data中的值换成设置的值
     * @param data 原数据
     */
    static filterData(data: MyObject) {
        if (!data || !Object.keys(data).length) {
            return {};
        }
        data = JSON.parse(JSON.stringify(data));
        let filterValues = Object.values(filterJson);
        filterValues.forEach((item, index) => {
            let keys = item['keys'];
            let regular = item['regular'];
            let replaceString = item['replaceString'];
            if (keys?.length && regular?.length && replaceString?.length) {
                data = this.filterDataByKeys(data, keys, regular, replaceString);
            }
        });

        return data;
    }

    /**
     * 用正则过滤data中对应keys的值
     * @param data data
     * @param keys 需要filter的keys
     * @param regular filter用正则表达式
     * @param replaceString filter用替代字符串
     */
    static filterDataByKeys(data: MyObject, keys: string[], regular: string, replaceString: string) {
        if (!data || !Object.keys(data).length) {
            return {};
        }

        let dataKeys = Object.keys(data);
        dataKeys.forEach((key, index) => {
            let value = data[key];

            if (typeof value == 'object') {
                data[key] = this.filterDataByKeys(value, keys, regular, replaceString);
            } else if (typeof value == 'string') {
                let contained = keys.some((item) => item === key);
                if (contained) {
                    let regexp = new RegExp(regular, 'g');
                    data[key] = value.replace(regexp, replaceString);
                }
            }
        });

        return data;
    }
}

interface NativeData {
    customerRole?: string;
    language?: string;
    longTermSession?: {
        expireDate: string;
        expiresIn: string;
        refreshToken: string;
        accessToken: string;
    };
    pps?: string;
    remember?: boolean;
    session?: {
        accessToken: string;
        expireDate: string;
        expiresIn: string;
        refreshToken: string;
    };
}
interface DefaultClientInfo {
    appVersion: string;
    language: string;
    pps: string;
    scope: string;
}

/**
 * Provding methods and events mapping of tracking.
 */
class AppInsightsHelper {
    defaultClientInfo: DefaultClientInfo = {
        appVersion: '',
        language: '',
        pps: '',
        scope: '',
    };

    constructor(scope: string) {
        this.defaultClientInfo.scope = scope;
    }

    combineClientProps = async (params?: MyObject) => {
        let { appVersion, pps, language, scope, ...other } = this.defaultClientInfo;
        //Ajax scope 由params傳過來，替換創建AppInsights時的scope
        scope = params?.scope || scope;
        let properties = { ...other };
        if (params) {
            const { scope, ...other } = params;
            properties = { ...properties, ...other };
        }
        let result: any = { appVersion, pps, language, scope, properties };
        return result;
    };

    /**
     * Track action event
     * @param {string} eventName - Event name
     * @param {object} params - Customized information
     */
    trackEvent = async (eventName: string, params: MyObject) => {
        try {
            if (!appInsights) {
                console.log('trackEvent appInsightsInstance not initialized');
                return;
            }

            let props = await this.combineClientProps(params);
            props = AppInsightsFilter.filterData(props);
            // console.log('trackEvent', eventName, props);

            (await appInsights).trackEvent({
                name: eventName,
                properties: props,
            });
        } catch (err) {
            console.error(err);
        }
    };

    /**
     * Track page view
     * @param {string} pageName - Page name
     * @param {object} params - Customized information
     */
    trackPageView = async (pageName: string, params: MyObject) => {
        try {
            if (!appInsights) {
                return;
            }
            let props = await this.combineClientProps(params);
            props = AppInsightsFilter.filterData(props);
            // console.log('trackPageView', pageName, props);

            (await appInsights).trackPageView({
                name: pageName,
                properties: props,
            });
        } catch (err) {
            console.error(err);
        }
    };

    /**
     * Track exception
     * @param {string} errorMessage - Exception message.
     */
    trackException = async (errorMessage: string) => {
        let properties = await this.combineClientProps();
        properties = AppInsightsFilter.filterData(properties);
        (await appInsights).trackException({
            exception: new Error(errorMessage),
            properties,
        });
    };
}

export default AppInsightsHelper;

const filterJson = {
    mobile: {
        keys: ['mobileNo', 'mobile', 'homeNo', 'value'],
        regular: '[0-9]+(\\d{4})',
        replaceString: '****$1',
    },
    userName: {
        keys: [
            'cusName',
            'firstName',
            'lastName',
            'surname',
            'givenName',
            'userName',
            'username',
            'biometricRegisteredPps',
        ],
        regular: '([\\s\\S]{2})[\\s\\S]*([\\s\\S]{2})',
        replaceString: '$1****$2',
    },
    email: {
        keys: ['email', 'emailAddress', 'emailAccount', 'value'],
        regular: '^(\\w{2}|\\w{1})(\\w)*([\\.-]?\\w+)*(@(?:[\\w](?:[\\w-]*[\\w])?\\.)+[\\w](?:[\\w-]*[\\w])?)',
        replaceString: '$1****$4',
    },
    accessToken: {
        keys: ['accessToken', 'accesstoken', 'Authorization', 'refreshToken'],
        regular: '([.*-]+).*(.{4})',
        replaceString: '$1****$2',
    },
    password: {
        keys: ['password', 'oldPassword', 'newPassword', 'confirmPassword', 'rePassword'],
        regular: '^.*$',
        replaceString: '******',
    },
};
export interface MyObject {
    [key: string]: any;
}

export { reactPlugin, appInsights };
